import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {Button, Modal, Spin} from "antd";
import {refreshSession} from "../../actions/api";
import {ClockCircleOutlined, WarningTwoTone} from "@ant-design/icons";
import "./SessionTimeoutProvides.css"

const MILLI = 1;
const SECONDS = 1000 * MILLI;
const MINUTES = 60 * SECONDS;
const DEFAULT_TIMEOUT_WARNING = 5 * MINUTES;
const DEFAULT_CHECK_TIMEOUT_EVERY_MS = 1 * MINUTES;

export const COOKIE_EXPIRY_NAME = "session_expiry_time";
export const MODAL_DATA_TESTID = "session-expiration-warning"

interface SessionTimeoutProviderProps {
    children: ReactNode;
    checkTimeoutEveryMs?: number,
    msRemainingBeforeWarningAppears?: number
}

const getCookieValue = (name: string) => {
    const cookies = document.cookie.split(';').map(cookie => cookie.trim());
    for (const cookie of cookies) {
        const [key, value] = cookie.split('=');
        if (key === name) return value;
    }
    return null;
}

const getTimeOfTimeout = () => {
    const value = getCookieValue(COOKIE_EXPIRY_NAME);
    if (value) {
        return Number.parseInt(value)
    }
    return null;
}

const getMilliSecondsToTimeout = (date: Date) => {
    const now = new Date();
    const timeLeft = date.getTime() - now.getTime();
    return timeLeft;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({children, checkTimeoutEveryMs, msRemainingBeforeWarningAppears}) => {
    const [isTimeoutApproaching, setTimeoutApproaching] = useState<{
        isNear: boolean,
        showModal: boolean,
        timeLeft: number | null
    }>({isNear: false, showModal: false, timeLeft: null});
    const intervalBetweenTimeoutChecks = useRef(checkTimeoutEveryMs || DEFAULT_CHECK_TIMEOUT_EVERY_MS)
    const timeUntilWarningAppears = useRef(msRemainingBeforeWarningAppears || DEFAULT_TIMEOUT_WARNING);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(0);

    const deleteCookie = (name: string) => {
        document.cookie = name + '=; Max-Age=-1; path=/';
    }

    useEffect(() => {
        const timeToTimeout = getTimeOfTimeout();
        if (timeToTimeout) {
            if (timeToTimeout < new Date().getTime()) {
                // if cookie is old on load. remove it
                deleteCookie(COOKIE_EXPIRY_NAME);
            }
        }
        if (window.location.pathname.includes("login.do")) {
            deleteCookie(COOKIE_EXPIRY_NAME);
        }
    }, []);

    useEffect(() => {
        if (window.location.pathname.includes("login.do")) {
            deleteCookie(COOKIE_EXPIRY_NAME);
        }
        setTimeout(() => {
            const timeToTimeout = getTimeOfTimeout();
            const timeLeft = timeToTimeout && getMilliSecondsToTimeout(new Date(timeToTimeout));
            if (timeLeft && (timeLeft - intervalBetweenTimeoutChecks.current) < timeUntilWarningAppears.current && (isTimeoutApproaching.isNear === false)) {
                setTimeoutApproaching({
                    isNear: true,
                    showModal: false,
                    timeLeft: timeLeft
                })
            }
            setLastUpdate(new Date().getTime());
        }, intervalBetweenTimeoutChecks.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpdate]);

    useEffect(() => {
        if (isTimeoutApproaching.isNear === false) {
            return;
        }
        setTimeout(() => {
            const timeToTimeout = getTimeOfTimeout();
            if (timeToTimeout === null) {
                // if cookie removed, remove modal
                setTimeoutApproaching({
                    isNear: false,
                    timeLeft: null,
                    showModal: false
                });
                return;
            }
            const timeLeft = getMilliSecondsToTimeout(new Date(timeToTimeout));
            // if timer is close to the warning, start counting every second
            if ((timeLeft - intervalBetweenTimeoutChecks.current) < timeUntilWarningAppears.current) {
                // only show the modal though when the limit is reached.
                let showModal = timeLeft < timeUntilWarningAppears.current;
                setTimeoutApproaching({
                    isNear: true,
                    timeLeft: timeLeft,
                    showModal: showModal
                })
            } else {
                if (isTimeoutApproaching.isNear) {
                    // if the cookie has been renewed, stop checking every second
                    setTimeoutApproaching({
                        isNear: false,
                        timeLeft: null,
                        showModal: false,
                    });
                }
            }
        }, 1000);
    }, [isTimeoutApproaching])

    const handler = async () => {
        setLoading(true);
        await refreshSession().catch(console.error).finally(() => setLoading(false));
        let redirectToLogin = false;
        const timeToTimeout =  getTimeOfTimeout();
        if (timeToTimeout == null) {
            return
        } else {
            const timeLeft = getMilliSecondsToTimeout(new Date(timeToTimeout));
            if (timeLeft <= (msRemainingBeforeWarningAppears || DEFAULT_TIMEOUT_WARNING)) {
                redirectToLogin = true;
            }
        }
        if (redirectToLogin) {
            deleteCookie(COOKIE_EXPIRY_NAME);
            setTimeoutApproaching({
                isNear: false,
                timeLeft: null,
                showModal: false
            })
            window.location.href =  '/login.do';
        }
    };

    const minsLeft = isTimeoutApproaching.timeLeft === null ? null : Math.max(Math.floor((isTimeoutApproaching.timeLeft/1000)/60),0).toString();
    const secondsLeft = isTimeoutApproaching.timeLeft === null ? null : Math.max(Math.floor((isTimeoutApproaching.timeLeft/1000)%60),0).toString().padStart(2, "0");

    return <>
        {children}
        {isTimeoutApproaching.showModal && (isTimeoutApproaching.timeLeft && isTimeoutApproaching.timeLeft> 0 ? <Modal className={"session-expiring"} data-testid={MODAL_DATA_TESTID} title={"Session Expiring"} open={isTimeoutApproaching.showModal} onOk={handler} onCancel={handler} closeIcon={null} footer={null}  >
            <div style={{display: "flex", flexDirection: "row"}}>
                <div style={{flex: "0 0 48px"}}><ClockCircleOutlined style={{ fontSize: "32px", color: "#d8665c"}}/></div>
                <div style={{ display: "flex", flexDirection: "column"}}>
                    <p>You will be logged out after 5 minutes due to inactivity</p>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "16px", marginTop: "16px"}}>
                        <Button disabled={loading} onClick={handler}>
                            {loading && <Spin style={{marginRight: "8px"}}/>}
                            Stay Logged in
                        </Button>
                        <span>Countdown: {minsLeft}:{secondsLeft}</span>
                    </div>
                </div>
            </div>
            </Modal>
         : <Modal className={"session-expiring"} data-testid={MODAL_DATA_TESTID} title={"You've Been Logged Out Due To Inactivity"} open={isTimeoutApproaching.showModal} onOk={handler} onCancel={handler} closeIcon={null} footer={null} >
                <div style={{display: "flex", flexDirection: "row"}}>
                    <div style={{flex: "0 0 48px"}}><WarningTwoTone twoToneColor={['#000', '#FFEC3D']} style={{ fontSize: "32px"}}/></div>
                    <div style={{ display: "flex", flexDirection: "column"}}>
                        <span>Your session has expired due to inactivity.</span>
                        <span>Please log back in to continue working.</span>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "16px", marginTop: "16px"}}>
                            <Button disabled={loading} onClick={handler}>
                                {loading && <Spin style={{marginRight: "8px"}}/>}
                                Log Back in
                            </Button>
                            <span>Countdown: {minsLeft}:{secondsLeft}</span>
                        </div>
                    </div>
                </div>
            </Modal>)}
    </>
};